'use client';

import { Resource } from 'i18next';
import { ReactNode } from 'react';

import { StyledComponentsRegistry } from '@/components/StyledComponentsRegistry';
import { ParticipantEnum } from '@/constants/common';
import { ApolloProvider } from '@/providers/ApolloProvider';
import { LoaderProvider } from '@/providers/LoaderProvider';
import { ParticipantProvider } from '@/providers/ParticipantProvider';
import { ProfileModeProvider } from '@/providers/ProfileModeProvider/ProfileModeProvider';
import { ServerNowProvider } from '@/providers/ServerNowProvider';
import { SnackbarProvider } from '@/providers/SnackbarProvider';
import { ThemeProvider } from '@/providers/ThemeProvider/ThemeProvider';
import { TranslationProvider } from '@/providers/TranslationProvider';
import { GlobalStyles } from '@/styles/globalStyles';
import { CookiesUtils } from '@/utils/helpers/cookies';

import { TopProgressBarProvider } from './TopProgressBarProvider';

type ProvidersProps = {
  children: ReactNode;
  params: {
    locale: string;
    resources: Resource;
    ssrCookie: string;
    initialParticipant: ParticipantEnum;
    themeCookie: string;
    serverNow: number;
  };
};

export const Providers = ({ children, params }: ProvidersProps) => {
  const { locale, resources, themeCookie, serverNow } = params;

  const cookieDomain = CookiesUtils.getDomain();

  return (
    <StyledComponentsRegistry>
      <ServerNowProvider serverNow={serverNow}>
        <ThemeProvider cookieDomain={cookieDomain} initialTheme={themeCookie}>
          <ParticipantProvider
            cookieDomain={cookieDomain}
            initialParticipant={params.initialParticipant}
          >
            <GlobalStyles />
            <TranslationProvider locale={locale} resources={resources}>
              <ApolloProvider locale={locale} ssrCookie={params.ssrCookie}>
                <ProfileModeProvider>
                  <SnackbarProvider>
                    <LoaderProvider>
                      <TopProgressBarProvider>
                        {children}
                      </TopProgressBarProvider>
                    </LoaderProvider>
                  </SnackbarProvider>
                </ProfileModeProvider>
              </ApolloProvider>
            </TranslationProvider>
          </ParticipantProvider>
        </ThemeProvider>
      </ServerNowProvider>
    </StyledComponentsRegistry>
  );
};
